import { ASSET_SEARCH_VISIBILITY_OPTIONS } from './constants/asset-taxonomies';
import { isTaxonomyFilterAll, normalizeTaxonomyFilterValues } from './asset-taxonomy.util';
import type { AssetSearchVisibilityApi, AssetTypeApi } from './models/asset-api.models';
import type {
  AssetAssistantSearchStateDto,
  AssetAssistantSuggestedFilterDto,
} from './models/asset-assistant-api.models';

export type AssetLibraryFilterChipState = 'applied' | 'suggested' | 'available';

const CHIP_STATE_SORT_ORDER: Record<AssetLibraryFilterChipState, number> = {
  applied: 0,
  suggested: 1,
  available: 2,
};

const DEFAULT_VISIBILITY: AssetSearchVisibilityApi = 'PublishedOnly';

export function resolveVisibilityValue(value: string): AssetSearchVisibilityApi | null {
  const normalized = normalizeFilterValue(value);
  for (const option of ASSET_SEARCH_VISIBILITY_OPTIONS) {
    if (
      normalizeFilterValue(option.value) === normalized ||
      normalizeFilterValue(option.label) === normalized
    ) {
      return option.value;
    }
  }
  return null;
}

function effectiveVisibility(state: AssetAssistantSearchStateDto): AssetSearchVisibilityApi {
  return state.visibility ?? DEFAULT_VISIBILITY;
}

export function normalizeFilterValue(value: string): string {
  return value.trim().toLowerCase();
}

export function filterValuesMatch(left: string, right: string): boolean {
  return normalizeFilterValue(left) === normalizeFilterValue(right);
}

function effectiveIndustries(state: AssetAssistantSearchStateDto): string[] {
  return normalizeTaxonomyFilterValues(state.industries);
}

function effectivePractices(state: AssetAssistantSearchStateDto): string[] {
  return normalizeTaxonomyFilterValues(state.practices);
}

export function isFilterApplied(
  state: AssetAssistantSearchStateDto | null,
  key: string,
  value: string,
): boolean {
  if (!state) return false;

  switch (key) {
    case 'type':
      return (state.types ?? []).some((type) => filterValuesMatch(String(type), value));
    case 'industry':
      if (isTaxonomyFilterAll(value)) {
        return effectiveIndustries(state).length === 0;
      }
      return effectiveIndustries(state).some((industry) => filterValuesMatch(industry, value));
    case 'practice':
      if (isTaxonomyFilterAll(value)) {
        return effectivePractices(state).length === 0;
      }
      return effectivePractices(state).some((practice) => filterValuesMatch(practice, value));
    case 'goldenOnly':
      return state.goldenOnly === true && normalizeFilterValue(value) === 'true';
    case 'visibility': {
      const target = resolveVisibilityValue(value);
      return target !== null && effectiveVisibility(state) === target;
    }
    default:
      return false;
  }
}

export function isFilterSuggested(
  suggestedFilters: readonly AssetAssistantSuggestedFilterDto[],
  key: string,
  value: string,
): boolean {
  return suggestedFilters.some((filter) => {
    if (filter.key !== key) return false;
    if (key === 'visibility') {
      const target = resolveVisibilityValue(value);
      const suggested = resolveVisibilityValue(filter.value);
      return target !== null && suggested !== null && target === suggested;
    }
    return filterValuesMatch(filter.value, value);
  });
}

export function resolveFilterChipState(
  state: AssetAssistantSearchStateDto | null,
  suggestedFilters: readonly AssetAssistantSuggestedFilterDto[],
  key: string,
  value: string,
): AssetLibraryFilterChipState {
  if (isFilterApplied(state, key, value)) return 'applied';
  if (isFilterSuggested(suggestedFilters, key, value)) return 'suggested';
  return 'available';
}

export function sortFilterChipsByState<
  T extends { value: string; label: string; filterKey?: string },
>(
  chips: readonly T[],
  groupKey: string,
  state: AssetAssistantSearchStateDto | null,
  suggestedFilters: readonly AssetAssistantSuggestedFilterDto[],
): T[] {
  return [...chips].sort((left, right) => {
    const leftKey = left.filterKey ?? groupKey;
    const rightKey = right.filterKey ?? groupKey;
    const leftOrder =
      CHIP_STATE_SORT_ORDER[resolveFilterChipState(state, suggestedFilters, leftKey, left.value)];
    const rightOrder =
      CHIP_STATE_SORT_ORDER[resolveFilterChipState(state, suggestedFilters, rightKey, right.value)];
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return left.label.localeCompare(right.label);
  });
}

export function toggleFilterInSearchState(
  state: AssetAssistantSearchStateDto,
  key: string,
  value: string,
): AssetAssistantSearchStateDto {
  switch (key) {
    case 'type': {
      const types = [...(state.types ?? [])];
      const index = types.findIndex((type) => filterValuesMatch(String(type), value));
      if (index >= 0) {
        types.splice(index, 1);
      } else {
        types.push(value as AssetTypeApi);
      }
      return { ...state, types: types.length > 0 ? types : null };
    }
    case 'industry': {
      if (isTaxonomyFilterAll(value)) {
        return { ...state, industries: null };
      }

      const industries = [...effectiveIndustries(state)];
      const index = industries.findIndex((industry) => filterValuesMatch(industry, value));
      if (index >= 0) {
        industries.splice(index, 1);
      } else {
        industries.push(value);
      }
      return { ...state, industries: industries.length > 0 ? industries : null };
    }
    case 'practice': {
      if (isTaxonomyFilterAll(value)) {
        return { ...state, practices: null };
      }

      const practices = [...effectivePractices(state)];
      const index = practices.findIndex((practice) => filterValuesMatch(practice, value));
      if (index >= 0) {
        practices.splice(index, 1);
      } else {
        practices.push(value);
      }
      return { ...state, practices: practices.length > 0 ? practices : null };
    }
    case 'goldenOnly':
      return { ...state, goldenOnly: state.goldenOnly === true ? null : true };
    case 'visibility': {
      const target = resolveVisibilityValue(value);
      if (!target) return state;
      const current = effectiveVisibility(state);
      const next = current === target ? DEFAULT_VISIBILITY : target;
      return { ...state, visibility: next };
    }
    default:
      return state;
  }
}

/** Round-trip state for assistant calls; normalizes query and visibility default. */
export function prepareSearchStateForAssistant(
  state: AssetAssistantSearchStateDto,
): AssetAssistantSearchStateDto {
  const query = state.query?.trim();
  if (!query) {
    throw new Error('Search state requires a non-empty query.');
  }

  return {
    ...state,
    visibility: effectiveVisibility(state),
    industries: effectiveIndustries(state),
    practices: effectivePractices(state),
    query,
  };
}

export function searchStateHasQuery(state: AssetAssistantSearchStateDto | null): boolean {
  return !!state?.query?.trim();
}
